'use client'

import {UiErrorText} from "@/components/i18n/ui-error-text"
import { useState } from 'react'
import { History, Loader2 } from 'lucide-react'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import {
  Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle,
} from '@/components/ui/dialog'
import { useBillingPlanHistoryQuery } from '@/hooks/queries/billing-plan-history'
import type { PlanHistoryEntry } from '@/lib/billing/plans/plan-history'
import type { PlanOperation } from '@/lib/billing/plans/plan-write'
import { describePlanChanges, formatPlanTimestamp } from './change-format'
import { ChangeList } from './change-list'
import { PlanDetails } from './plan-details'
import { useTranslations } from "next-intl"
import { TranslationText } from "@/components/i18n/translation-text"
import { usePlanPresentation } from "./use-plan-presentation"





export function PlanHistoryButton({ planId }: { planId: string }) {
  const tUi = useTranslations("Billing.BillingPlanEditorHistoryDialog")

  const [open, setOpen] = useState(false)
  return <>
    <Button type="button" variant="outline" onClick={() => setOpen(true)}><TranslationText>{tUi.rich("tag0Tag0History", { tag0: () => (<History className="mr-2 h-4 w-4" />) })}</TranslationText></Button>
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent className="sm:max-w-2xl">
        <DialogHeader>
          <DialogTitle><span key={tUi("planHistory")}>{tUi("planHistory")}</span></DialogTitle>
          <DialogDescription><span key={tUi("changesSavedByAdministratorsNewestFirst")}>{tUi("changesSavedByAdministratorsNewestFirst")}</span></DialogDescription>
        </DialogHeader>
        {open ? <Timeline planId={planId} /> : null}
      </DialogContent>
    </Dialog>
  </>
}

function Timeline({ planId }: { planId: string }) {
  const tUi = useTranslations("Billing.BillingPlanEditorHistoryDialog")

  const { data, error, isPending } = useBillingPlanHistoryQuery(planId)
  if (isPending) {
    // Own element: the loaded states reuse this <p> (browser translation).
    return <p role="status" className="flex items-center gap-2 text-sm text-muted-foreground"><Loader2 className="h-4 w-4 animate-spin" /><span><span key={tUi("loadingHistory")}>{tUi("loadingHistory")}</span></span></p>
  }
  if (error) return <p key={error.message || tUi('couldNotLoadHistory')} role="alert" className="text-sm text-destructive"><UiErrorText message={error.message} fallback={tUi('couldNotLoadHistory')} /></p>
  if (data.entries.length === 0) {
    return <p className="text-sm text-muted-foreground"><span key={tUi("noRecordedChangesYetChangesMadeBeforeHistory")}>{tUi("noRecordedChangesYetChangesMadeBeforeHistory")}</span></p>
  }
  return (
    <div className="space-y-4">
      <ol className="space-y-5 border-l pl-5">
        {data.entries.map(entry => <Entry key={entry.id} entry={entry} />)}
      </ol>
      {data.truncated ? <p className="text-xs text-muted-foreground"><span key={tUi("onlyTheMostRecentChangesAreShown")}>{tUi("onlyTheMostRecentChangesAreShown")}</span></p> : null}
    </div>
  )
}

function Entry({ entry }: { entry: PlanHistoryEntry }) {
  const presentation = usePlanPresentation()


  const tUi = useTranslations("Billing.BillingPlanEditorHistoryDialog")

  const rows = describePlanChanges(entry.changes, presentation)
  return (
    <li className="relative min-w-0 space-y-3">
      <span aria-hidden="true" className="absolute -left-[1.6rem] top-1.5 h-2.5 w-2.5 rounded-full bg-primary" />
      <div className="flex flex-wrap items-center gap-x-2 gap-y-1 text-sm">
        <Badge variant="secondary">{operationLabel(entry.operation, tUi)}</Badge>
        <time dateTime={entry.createdAt}>{formatPlanTimestamp(entry.createdAt, presentation)}</time>
        <span className="min-w-0 break-all text-muted-foreground">{<span key={tUi("byValue0Value1", { value0: String(entry.actorName), value1: String(entry.actorEmail ? ` (${entry.actorEmail})` : '') })}>{tUi("byValue0Value1", { value0: String(entry.actorName), value1: String(entry.actorEmail ? ` (${entry.actorEmail})` : '') })}</span>}</span>
      </div>
      {entry.operation === 'create'
        ? <details className="rounded-md border p-3">
          <summary className="cursor-pointer text-sm font-semibold"><span key={tUi("initialSettings")}>{tUi("initialSettings")}</span></summary>
          <div className="mt-3"><PlanDetails rows={rows.map(row => ({ ...row, value: row.to, inactive: false }))} /></div>
        </details>
        : <ChangeList rows={rows} />}
    </li>
  )
}

function operationLabel(operation: PlanOperation, tUi: ReturnType<typeof useTranslations<'Billing.BillingPlanEditorHistoryDialog'>>): string {
  switch (operation) {
    case 'create': return tUi('created')
    case 'update': return tUi('updated')
    case 'archive': return tUi('archived')
    case 'restore': return tUi('restored')
  }
}
